import React, { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  FaBook,
  FaBullhorn,
  FaEnvelope,
  FaHome,
  FaQuestionCircle,
  FaUsers,
  FaVideo,
} from "../utils/icons";
import { decodeJwtPayload } from "../utils/jwt";
import { API } from "../api";
import { initSocket } from "../utils/socket";

type NavItem = {
  label: string;
  path: string;
  icon: React.ComponentType<any>;
};

const studentItems: NavItem[] = [
  { label: "Home", path: "/student", icon: FaHome as React.ComponentType<any> },
  { label: "Videos", path: `__COURSE__`, icon: FaVideo as React.ComponentType<any> },
  { label: "Messages", path: "/student/messages", icon: FaEnvelope as React.ComponentType<any> },
  { label: "Updates", path: "/student/announcements", icon: FaBullhorn as React.ComponentType<any> },
  { label: "Doubts", path: "/student/doubts", icon: FaQuestionCircle as React.ComponentType<any> },
];

const adminItems: NavItem[] = [
  { label: "Home", path: "/admin", icon: FaHome as React.ComponentType<any> },
  { label: "Courses", path: "/admin/course-management", icon: FaBook as React.ComponentType<any> },
  { label: "Videos", path: "/admin/video-management", icon: FaVideo as React.ComponentType<any> },
  { label: "Students", path: "/admin/student-management", icon: FaUsers as React.ComponentType<any> },
  { label: "Doubts", path: "/admin/doubts", icon: FaQuestionCircle as React.ComponentType<any> },
];

export default function MobileBottomNav() {
  const location = useLocation();
  const token = localStorage.getItem("token");
  const isPublicPage = location.pathname === "/" || location.pathname === "/register";
  const [messageCount, setMessageCount] = useState(0);
  const [announcementCount, setAnnouncementCount] = useState(0);
  const [doubtCount, setDoubtCount] = useState(0);
  const [videoCount, setVideoCount] = useState(0);

  const payload = token ? decodeJwtPayload(token) : null;
  const shouldShow = Boolean(token && payload && !isPublicPage);
  const role = (payload?.role || "").toLowerCase();
  const userId = payload && (payload.id || payload.userId || payload.user);

  const courseId = localStorage.getItem("selectedCourseId") || "";

  useEffect(() => {
    document.body.classList.toggle("mobile-bottom-nav-visible", shouldShow);
    return () => {
      document.body.classList.remove("mobile-bottom-nav-visible");
    };
  }, [shouldShow]);

  useEffect(() => {
    if (!shouldShow || role !== "student") return;

    const loadCounts = async () => {
      try {
        const res = await API.get<{
          id: number; title: string; message: string; is_read: number; created_at: string;
        }[]>("/student/notifications");

        setMessageCount(res.data.filter((n) =>
          n.is_read === 0 && (n.title.toLowerCase().includes("message") || n.title.toLowerCase().includes("dr"))
        ).length);
        setAnnouncementCount(res.data.filter((n) =>
          n.is_read === 0 && n.title.toLowerCase().includes("announcement")
        ).length);
        setDoubtCount(res.data.filter((n) =>
          n.is_read === 0 && n.title.toLowerCase().includes("doubt")
        ).length);
        setVideoCount(res.data.filter((n) =>
          n.is_read === 0 && n.title.toLowerCase().includes("video")
        ).length);
      } catch (err) {
        console.error("Unable to load mobile notification counts", err);
      }
    };

    loadCounts();

    const socket = initSocket();

    const handleNewMessage = () => setMessageCount((prev) => prev + 1);
    const handleNewAnnouncement = () => setAnnouncementCount((prev) => prev + 1);
    const handleDoubtResolved = (data: any) => {
      if (!userId || !data?.user_id || Number(data.user_id) !== Number(userId)) return;
      setDoubtCount((prev) => prev + 1);
    };
    const handleNewVideo = (data: any) => {
      const selectedCourseId = Number(localStorage.getItem("selectedCourseId"));
      if (selectedCourseId && data?.course_id && Number(data.course_id) === selectedCourseId) {
        setVideoCount((prev) => prev + 1);
      }
    };
    const handleNotificationsCleared = (data: any) => {
      if (!userId || !data?.user_id || Number(data.user_id) !== Number(userId)) return;
      if (!data.category || data.category === "all") {
        setMessageCount(0);
        setAnnouncementCount(0);
        setDoubtCount(0);
        setVideoCount(0);
      } else if (data.category === "message") {
        setMessageCount(0);
      } else if (data.category === "announcement") {
        setAnnouncementCount(0);
      } else if (data.category === "doubt") {
        setDoubtCount(0);
      } else if (data.category === "video") {
        setVideoCount(0);
      }
    };

    socket.on("new_message", handleNewMessage);
    socket.on("new_announcement", handleNewAnnouncement);
    socket.on("doubt_resolved", handleDoubtResolved);
    socket.on("new_video", handleNewVideo);
    socket.on("notifications_cleared", handleNotificationsCleared);

    return () => {
      socket.off("new_message", handleNewMessage);
      socket.off("new_announcement", handleNewAnnouncement);
      socket.off("doubt_resolved", handleDoubtResolved);
      socket.off("new_video", handleNewVideo);
      socket.off("notifications_cleared", handleNotificationsCleared);
    };
  }, [shouldShow, role, token, userId]);

  useEffect(() => {
    if (!shouldShow || role !== "student") return;

    if (location.pathname === "/student/messages") {
      setMessageCount(0);
      API.post("/student/notifications/mark-read", { category: "message" }).catch(() => {});
    }
    if (location.pathname === "/student/announcements") {
      setAnnouncementCount(0);
      API.post("/student/notifications/mark-read", { category: "announcement" }).catch(() => {});
    }
    if (location.pathname === "/student/doubts") {
      setDoubtCount(0);
      API.post("/student/notifications/mark-read", { category: "doubt" }).catch(() => {});
    }
    if (location.pathname.startsWith("/course/")) {
      setVideoCount(0);
      API.post("/student/notifications/mark-read", { category: "video" }).catch(() => {});
    }
  }, [location.pathname, shouldShow, role]);

  if (!shouldShow || !payload) return null;

  const items = role === "admin"
    ? adminItems
    : studentItems.map((item) =>
        item.path === "__COURSE__" ? { ...item, path: `/course/${courseId}` } : item
      );

  const getBadge = (path: string) => {
    if (role !== "student") return 0;
    if (path === "/student/messages") return messageCount;
    if (path === "/student/announcements") return announcementCount;
    if (path === "/student/doubts") return doubtCount;
    if (path.startsWith("/course/")) return videoCount;
    return 0;
  };

  return (
    <nav data-tour="lms-mobile-nav" className="mobile-bottom-nav" aria-label="Mobile navigation">
      {items.map((item) => {
        const Icon = item.icon;
        const slug = item.path.split("/").pop() || "home";
        const badge = getBadge(item.path);
        return (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.path === "/student" || item.path === "/admin"}
            data-tour={`lms-mobile-${slug}`}
            className={({ isActive }) =>
              `mobile-bottom-nav__item${isActive ? " active" : ""}`
            }
          >
            <span className="mobile-bottom-nav__icon">
              <Icon />
              {badge > 0 && (
                <span className="mobile-bottom-nav__badge">
                  {badge > 99 ? "99+" : badge}
                </span>
              )}
            </span>
            <span>{item.label}</span>
          </NavLink>
        );
      })}
    </nav>
  );
}
